import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ClipboardEdit, Loader2, ShieldAlert, Undo2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { invalidateTreatmentData } from "@/lib/treatmentDataInvalidation";
import { DATOS_EXPLOTACION_KEY } from "@/hooks/useDatosExplotacion";
import { CUADERNO_JUNTA_KEY } from "@/hooks/useCuadernoJunta";
import { SIGPAC_NAVES_KEY } from "@/hooks/useSigpacNaves";
import { useCuadernoCampoEnabled } from "@/hooks/useCuadernoCampoEnabled";
import {
  cambioDeshacer,
  esCambioDelDocumento,
  ETIQUETA_PAGINA_AJUSTES,
  ETIQUETA_SECCION_AJUSTES,
  rutaAjustesCuaderno,
  seccionAjustes,
  validarCambio,
  type CambioCuaderno,
  type OpCuaderno,
} from "@/lib/cuaderno/cambios";
import { aplicarCambioCuaderno, estadoDelCambio, type ResultadoAplicar } from "@/lib/cuaderno/aplicarCambio";
import { formatSigpac } from "@/lib/cuaderno/validators";
import { equipmentRegime } from "@/lib/machinery/equipmentRules";
import { buildActionUrl, getAssistantAction } from "@/lib/assistantActions";
import { AssistantActionButtons, type AssistantActionItem } from "@/components/assistant/AssistantActionButtons";

/** Lo que dice cada operación de ajustes, línea a línea (lo que se guarda, en claro). */
function lineasDeAjuste(o: OpCuaderno, t: (k: string, o?: Record<string, unknown>) => string): string[] {
  const s = (k: string) => t(`ipmAssistant.cuadernoChange.settings.${k}`);
  switch (o.op) {
    case "set_legal_field": {
      const etiqueta = t(`cuadernoDatos.fields.${o.campo}`);
      // «Dirección» del titular y de la explotación se llaman igual en el formulario: aquí, con su dueño.
      return [`${o.campo.startsWith("titular_") ? `${s("holder")} · ` : ""}${etiqueta}: ${o.valor}`];
    }
    case "set_datos_finca":
      // Desde el chat solo sale la respuesta habitual (sin captaciones ni zonas); lo demás se escribe en Ajustes.
      return [
        `${t("cuadernoDatos.sections.environment")} · ${o.finca}: ${
          o.datos.captacion
            ? t("cuadernoDatos.env.captacion")
            : o.datos.zona === "total"
              ? `${t("cuadernoDatos.env.zona")}: ${t("cuadernoDatos.env.zonaTotal")}`
              : o.datos.zona === "parcial"
                ? `${t("cuadernoDatos.env.zona")}: ${t("cuadernoDatos.env.zonaParcial")}`
                : s("no22")
        }`,
      ];
    case "upsert_equipment": {
      const numero = o.roma ? `${equipmentRegime(o.tipo) === "censo" ? s("census") : s("roma")}: ${o.roma}` : null;
      if (o.equipment_id) return numero ? [numero] : [];
      return [`${s("newMachine")} · ${t(`applicationContext.machinery.types.${o.tipo}`)}`, numero ?? (equipmentRegime(o.tipo) === "exenta" ? "" : s("noNumber"))].filter(Boolean);
    }
    case "upsert_service_company":
      return [`${s("nif")}: ${o.nif}`, `${s("ropo")}: ${o.ropo}`, o.caduca ? `${s("expires")}: ${o.caduca}` : `${s("issued")}: ${o.expedido}`];
    case "set_member_license":
      return [
        `${s("ropo")}: ${o.ropo}`,
        `${s("licence")}: ${t(`applicationContext.applicators.licenseTypes.${o.carne}`)}`,
        `${s("expires")}: ${o.caduca}`,
        ...(o.nif ? [`${s("nif")}: ${o.nif}`] : []),
      ];
    case "set_sigpac":
      return [formatSigpac(o.codigos)];
    default:
      return [];
  }
}

type Estado = "idle" | "checking" | "applying" | "applied" | "discarded" | "error" | "undoing" | "undone";

/**
 * CU9: lo que esta sesión ya hizo con cada tarjeta del documento (por `change_id`).
 * Un re-render o un re-montaje de la lista no la vuelve a aplicar; al reabrir la
 * conversación (sesión nueva) lo dice la auditoría (`estadoDelCambio`).
 */
const hechoEnSesion = new Map<string, "applied" | "undone">();
/** Solo para las pruebas. */
export const olvidarCambiosDeSesion = () => hechoEnSesion.clear();

/** Motivos de rechazo de `apply_cuaderno_change` con texto propio en la tarjeta (`ipmAssistant.cuadernoChange.rejected.*`). */
const RECHAZOS_CON_TEXTO = new Set([
  "fitosanitario_registrado",
  "fitosanitario_por_categoria",
  // A2 (lista blanca de D16): se llama como un producto del MAPA, o no es ni abono ni auxiliar.
  "fitosanitario_por_nombre",
  "no_es_abono_ni_auxiliar",
  "otro_cuaderno",
]);

/**
 * La tarjeta «Cambios propuestos» del modo cuaderno (CQ4b · A20).
 *
 * CU7 (27-09-2026): lo que el cuaderno pregunta UNA vez (datos legales, máquina,
 * empresa de servicios, carné, SIGPAC) sale como «Guardar en Ajustes de la
 * empresa», con cada dato en su línea y dónde queda («Ajustes → Empresa →
 * Cuaderno de campo; no te lo volveré a preguntar»); tras aplicar, el botón a
 * ese apartado. Y el chat sigue solo con la siguiente pregunta (`onAplicado`).
 *
 * El asistente PROPONE; aquí se ve exactamente qué cambia y a cuántas líneas o
 * partes afecta, con hasta tres ejemplos, y NADA cambia hasta pulsar «Aplicar».
 * Aplica el navegador con la sesión de la persona (`aplicarCambioCuaderno`):
 * el borrador se abre con `ensure_cuaderno_draft` y se cambia con
 * `apply_cuaderno_change` y `p_expected_count = recuentoDeOps(ops)`; los partes,
 * por su edge con recuento previo; o los datos legales. Si los datos cambiaron
 * desde la propuesta, no se aplica y se pide volver a pedirlo; si la RPC
 * rechaza algo, se dice por qué (leído con `leerErrorCambio`, sin inventar).
 *
 * Los fitosanitarios que se pidió quitar salen como «se quedan»: nunca hay
 * botón para sacarlos (D16), ni a los registrados ni a los de categoría
 * fitosanitaria sin nº (a esos les falta el nº: se corrige el parte).
 */
export function AssistantCuadernoChangeCard({
  item,
  onAplicado,
  autoAplicar = false,
}: {
  item: Record<string, unknown>;
  /**
   * Tras un «Aplicar» que SALE BIEN, una sola vez por tarjeta: el chat sigue solo
   * con la siguiente pregunta del cuaderno (petición de Pablo, 27-09). Si falla,
   * no se llama. NUNCA en un cambio del documento (CU9): esos se aplican solos y no
   * disparan el seguimiento (sin bucles).
   */
  onAplicado?: (cambio: CambioCuaderno) => void;
  /**
   * CU9 (28-09-2026, «el documento es de la persona»): la tarjeta acaba de llegar en
   * ESTE turno (no es del historial). Si es un cambio del documento, se aplica sola,
   * sin botón, y enseña «Hecho» con «Deshacer».
   */
  autoAplicar?: boolean;
}) {
  const { t } = useTranslation();
  const { company, user } = useAuth();
  const qc = useQueryClient();
  const [estado, setEstado] = useState<Estado>("idle");
  const [resultado, setResultado] = useState<ResultadoAplicar | null>(null);
  // Un seguimiento por tarjeta: ni un doble toque ni un re-render lo repiten.
  const avisado = useRef(false);
  /* Como la tarjeta de descarga: sin el interruptor de la empresa o sin rol de
     admin o encargado no hay «Aplicar» (una tarjeta vieja del historial, con el
     cuaderno ya apagado, no escribe). La RLS y la RPC lo vuelven a mirar. */
  const interruptor = useCuadernoCampoEnabled();
  const v = useMemo(() => validarCambio(item), [item]);
  const valida = v.ok ? v.cambio : null;
  const documento = !!valida && esCambioDelDocumento(valida);
  const lanzado = useRef(false);

  /* CU9: un cambio del documento se aplica SOLO al llegar (una vez por tarjeta y
     sesión); una tarjeta del historial enseña lo que dice la auditoría. */
  useEffect(() => {
    if (!valida || !documento || lanzado.current) return;
    if (interruptor.loading || !interruptor.allowed || !company?.id) return;
    lanzado.current = true;
    const previo = hechoEnSesion.get(valida.change_id);
    if (previo) {
      setEstado(previo);
      return;
    }
    if (autoAplicar) {
      void aplicarCon(valida, "applied");
      return;
    }
    setEstado("checking");
    void estadoDelCambio(supabase, company.id, valida.change_id).then((e) => {
      setEstado(e === "hecho" ? "applied" : e === "deshecho" ? "undone" : "idle");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valida, documento, interruptor.loading, interruptor.allowed, company?.id, autoAplicar]);

  const invalidar = (c: CambioCuaderno) => {
    // T12: tocar partes invalida TODO lo que lee partes; y lo del cuaderno y los datos legales.
    if (c.tipo === "parte") invalidateTreatmentData(qc);
    if (c.tipo === "legal") qc.invalidateQueries({ queryKey: [DATOS_EXPLOTACION_KEY] });
    // CU7: lo que enseña Ajustes → Empresa → «Cuaderno de campo».
    if (c.tipo === "maquinaria") qc.invalidateQueries({ queryKey: ["company-equipment"] });
    if (c.tipo === "aplicador") qc.invalidateQueries({ queryKey: ["company-applicators"] });
    if (c.tipo === "sigpac") qc.invalidateQueries({ queryKey: [SIGPAC_NAVES_KEY] });
    // La tarjeta de descarga y la hoja de /spray-logs se recalculan con el cambio.
    qc.invalidateQueries({ queryKey: [CUADERNO_JUNTA_KEY] });
  };

  /** Aplica `cambio` (la tarjeta, o su «Deshacer») y deja la tarjeta en `fin` si sale bien. */
  async function aplicarCon(cambio: CambioCuaderno, fin: "applied" | "undone") {
    if (!company?.id) return;
    setEstado(fin === "undone" ? "undoing" : "applying");
    const r = await aplicarCambioCuaderno({ supabase, companyId: company.id, userId: user?.id ?? null }, cambio);
    setResultado(r);
    // Un «Deshacer» que falla deja la tarjeta como estaba (hecha), con el error debajo.
    setEstado(r.ok ? fin : fin === "undone" ? "applied" : "error");
    if (!r.ok) return;
    invalidar(cambio);
    if (esCambioDelDocumento(cambio)) {
      hechoEnSesion.set(cambio.change_id, fin);
      return;
    }
    if (!avisado.current) {
      avisado.current = true;
      onAplicado?.(cambio);
    }
  }

  if (!interruptor.loading && !interruptor.allowed) {
    return (
      <div data-testid="assistant-cuaderno-change-unavailable" className="mt-2 rounded-2xl border bg-white px-3 py-2 text-xs text-muted-foreground shadow-sm">
        {t("cuadernoJunta.notAvailable")}
      </div>
    );
  }

  if (!valida) {
    return (
      <div data-testid="assistant-cuaderno-change-invalid" className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        {t("ipmAssistant.cuadernoChange.invalid", "Esta propuesta ya no se puede aplicar: pídemela otra vez.")}
      </div>
    );
  }
  const c: CambioCuaderno = valida;
  const deshacer = documento ? cambioDeshacer(c) : null;

  /* CU7: lo de una sola vez se guarda en Ajustes. Los datos legales van en UNA tarjeta
     «Guardar en Ajustes de la empresa» con cada campo en su línea. */
  const esAjuste = c.tipo === "legal" || c.tipo === "maquinaria" || c.tipo === "aplicador" || c.tipo === "sigpac";
  const seccion = seccionAjustes(c);
  const carneDePersona = c.ops[0]?.op === "set_member_license" ? c.ops[0] : null;
  const titulo =
    c.tipo === "legal"
      ? t("ipmAssistant.cuadernoChange.settings.title")
      : t(`ipmAssistant.cuadernoChange.op.${c.operacion}`, { valor: c.valor_label ?? "" }).replace(/:\s*$/, "");
  const detalle = esAjuste ? c.ops.flatMap((o) => lineasDeAjuste(o, t)) : [];
  /* Tras aplicar, el botón a donde quedó: su grupo de la página «Cuaderno de
     campo» de Ajustes (`/settings/cuaderno?seccion=…`, desde el 27-09) o, el carné
     de una persona, su ficha del equipo. La URL la ponen el contrato y el servidor (el id de la persona lo
     resolvió la edge por nombre bajo RLS), nunca el texto del modelo. */
  const botones: AssistantActionItem[] = [];
  if (seccion) {
    botones.push({
      id: `cuaderno-ajustes-${seccion}`,
      label: `${t(ETIQUETA_PAGINA_AJUSTES)} · ${t(ETIQUETA_SECCION_AJUSTES[seccion])}`,
      url: rutaAjustesCuaderno(seccion),
    });
  } else if (carneDePersona) {
    const a = getAssistantAction("team_member_edit");
    const url = a ? buildActionUrl(a, { entityId: carneDePersona.user_id }) : null;
    if (url) botones.push({ id: "cuaderno-ajustes-carne", label: `${t("settings.team.editMember")} · ${carneDePersona.nombre}`, url });
  }
  const lineasDoc = new Set(c.ops.map((o) => ("linea" in o ? o.linea : "spray_log_entry_id" in o ? o.spray_log_entry_id : "")).filter(Boolean)).size;
  const cuenta =
    c.tipo === "parte"
      ? t("ipmAssistant.cuadernoChange.countApplications", { count: c.expected_count })
      : documento
        ? t("ipmAssistant.cuadernoChange.countLines", { count: lineasDoc })
        : null;

  const aplicar = async () => {
    if (!company?.id || estado === "applying" || estado === "undoing" || !interruptor.allowed) return;
    await aplicarCon(c, "applied");
  };
  const deshacerAhora = async () => {
    if (!deshacer || !company?.id || estado !== "applied" || !interruptor.allowed) return;
    await aplicarCon(deshacer, "undone");
  };

  const errorTexto = (r: ResultadoAplicar | null) => {
    // `"motivo" in r` y no `r.ok`: sin strictNullChecks, TS no estrecha la unión por un booleano.
    if (!r || !("motivo" in r)) return "";
    /* L4: cada tarea se cambia entera o nada (la edge escribe en una transacción), pero una
       tarjeta con VARIAS tareas va una a una: si falla a medias, se dice cuántos partes
       quedaron cambiados antes del fallo. */
    const parcial = r.aplicadas ? ` ${t("ipmAssistant.cuadernoChange.partial", { count: r.aplicadas })}` : "";
    return errorBase(r) + parcial;
  };
  const errorBase = (r: Extract<ResultadoAplicar, { ok: false }>) => {
    if (r.motivo === "recuento") return t("ipmAssistant.cuadernoChange.errorStale", "Los datos han cambiado desde la propuesta: pídemela otra vez.");
    if (r.motivo === "permiso") return t("ipmAssistant.cuadernoChange.errorPermission", "No tienes permiso para este cambio.");
    if (r.motivo === "invalida" && r.detalle === "asesor_sin_ropo") return t("ipmAssistant.cuadernoChange.settings.advisorRopo");
    if (r.motivo === "invalida") return t("ipmAssistant.cuadernoChange.invalid", "Esta propuesta ya no se puede aplicar: pídemela otra vez.");
    if (r.motivo === "rechazada") {
      // Lo que la RPC del borrador dijo, motivo a motivo; lo que no tiene texto propio, como propuesta no válida.
      const textos = [...new Set((r.rechazos ?? []).map((m) => (RECHAZOS_CON_TEXTO.has(m) ? `ipmAssistant.cuadernoChange.rejected.${m}` : "ipmAssistant.cuadernoChange.invalid")))];
      return (textos.length ? textos : ["ipmAssistant.cuadernoChange.invalid"]).map((k) => t(k)).join(" ");
    }
    return t("ipmAssistant.cuadernoChange.errorGeneric", "No se pudo aplicar. Inténtalo otra vez.");
  };

  return (
    <div data-testid="assistant-cuaderno-change-card" className="mt-2 w-full rounded-2xl border border-[#b6dded] bg-white px-3 py-2.5 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2E84DE] text-white">
          <ClipboardEdit className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("ipmAssistant.cuadernoChange.title", "Cambios propuestos")}
          </span>
          <span data-testid="assistant-cuaderno-change-summary" className="block text-sm font-bold text-foreground">{titulo}</span>
          {cuenta && <span data-testid="assistant-cuaderno-change-count" className="block text-xs text-muted-foreground">{cuenta}</span>}
        </span>
      </div>

      {detalle.length > 0 && (
        <ul data-testid="assistant-cuaderno-change-settings" className="mt-2 space-y-0.5 text-xs text-foreground/80">
          {detalle.map((l, i) => (
            <li key={i} className="break-words">
              {l}
            </li>
          ))}
        </ul>
      )}

      {esAjuste && estado !== "applied" && estado !== "discarded" && (
        <p data-testid="assistant-cuaderno-change-where" className="mt-2 text-[11px] text-muted-foreground">
          {t(carneDePersona ? "ipmAssistant.cuadernoChange.settings.whereMember" : "ipmAssistant.cuadernoChange.settings.where")}
        </p>
      )}

      {c.ejemplos.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-foreground/80">
          {c.ejemplos.map((e, i) => (
            <li key={i} className="truncate">
              {[e.fecha, e.nave, e.producto].filter(Boolean).join(" · ")}
            </li>
          ))}
        </ul>
      )}

      {c.bloqueadas && c.bloqueadas.length > 0 && (
        <p data-testid="assistant-cuaderno-change-blocked" className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
          <ShieldAlert className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>
            {t("ipmAssistant.cuadernoChange.blocked", "Se quedan en el cuaderno (son fitosanitarios): {{list}}", {
              list: c.bloqueadas.map((b) => b.producto).join(", "),
            })}
            {c.bloqueadas.some((b) => b.sin_numero) && (
              <span data-testid="assistant-cuaderno-change-blocked-no-number" className="block">
                {t("ipmAssistant.cuadernoChange.blockedNoNumber", "Sin nº de registro: {{list}}. Son fitosanitarios a los que les falta el nº; corrígelo en el parte.", {
                  list: c.bloqueadas.filter((b) => b.sin_numero).map((b) => b.producto).join(", "),
                })}
              </span>
            )}
          </span>
        </p>
      )}

      {c.avisos && c.avisos.length > 0 && (
        <ul data-testid="assistant-cuaderno-change-notices" className="mt-2 space-y-0.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
          {c.avisos.map((a, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <ShieldAlert className="mt-px h-3.5 w-3.5 shrink-0" />
              <span>{t(`ipmAssistant.cuadernoChange.notice.${a.codigo}`, { product: a.producto, number: a.numero ?? "" })}</span>
            </li>
          ))}
        </ul>
      )}

      {/* CU9: un cambio del DOCUMENTO se aplica solo: «Hecho» y «Deshacer», sin Aplicar/Descartar. */}
      {documento && (estado === "checking" || estado === "applying") && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        </p>
      )}
      {documento && (estado === "applied" || estado === "undoing") && (
        <div data-testid="assistant-cuaderno-change-done" className="mt-2 flex flex-wrap items-center gap-2">
          <p className="flex flex-1 items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {t("ipmAssistant.cuadernoChange.done")}
          </p>
          {deshacer && (
            <button
              type="button"
              onClick={deshacerAhora}
              disabled={estado === "undoing"}
              data-testid="assistant-cuaderno-change-undo"
              className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-xl border border-border px-3 text-[13px] font-semibold text-foreground transition-all active:scale-[0.98] disabled:opacity-60"
            >
              {estado === "undoing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
              {t("ipmAssistant.cuadernoChange.undo")}
            </button>
          )}
        </div>
      )}
      {documento && estado === "undone" && (
        <div data-testid="assistant-cuaderno-change-undone" className="mt-2 flex flex-wrap items-center gap-2">
          <p className="flex-1 text-xs text-muted-foreground">{t("ipmAssistant.cuadernoChange.undone")}</p>
          <button
            type="button"
            onClick={aplicar}
            data-testid="assistant-cuaderno-change-redo"
            className="inline-flex min-h-[40px] items-center justify-center rounded-xl border border-border px-3 text-[13px] font-semibold text-foreground transition-all active:scale-[0.98]"
          >
            {t("ipmAssistant.cuadernoChange.redo")}
          </button>
        </div>
      )}

      {!documento && estado === "applied" && (
        <p data-testid="assistant-cuaderno-change-applied" className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
          <CheckCircle2 className="h-4 w-4" />
          {resultado && "aviso" in resultado && resultado.aviso === "recuento_distinto"
            ? t("ipmAssistant.cuadernoChange.appliedDifferent", "Aplicado a {{count}} (se esperaban {{expected}}).", { count: resultado.aplicadas, expected: resultado.esperado })
            : t("ipmAssistant.cuadernoChange.applied", "Aplicado.")}
        </p>
      )}
      {estado === "applied" && esAjuste && (
        <div data-testid="assistant-cuaderno-change-saved">
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t(carneDePersona ? "ipmAssistant.cuadernoChange.settings.savedMember" : "ipmAssistant.cuadernoChange.settings.saved")}
          </p>
          <AssistantActionButtons items={botones} />
        </div>
      )}
      {estado === "discarded" && (
        <p className="mt-2 text-xs text-muted-foreground">{t("ipmAssistant.cuadernoChange.discarded", "Descartado: no se ha cambiado nada.")}</p>
      )}
      {resultado && !resultado.ok && estado !== "applying" && estado !== "undoing" && (
        <p data-testid="assistant-cuaderno-change-error" className="mt-2 text-xs font-medium text-destructive">{errorTexto(resultado)}</p>
      )}

      {(estado === "idle" || (estado === "applying" && !documento) || estado === "error") && (
        <div className="mt-2.5 flex gap-2">
          <button
            type="button"
            onClick={aplicar}
            disabled={estado === "applying" || interruptor.loading}
            data-testid="assistant-cuaderno-change-apply"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#2E84DE] px-3 text-[13px] font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {estado === "applying" && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("ipmAssistant.cuadernoChange.apply", "Aplicar")}
          </button>
          <button
            type="button"
            onClick={() => setEstado("discarded")}
            disabled={estado === "applying"}
            data-testid="assistant-cuaderno-change-discard"
            className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-xl border border-border px-3 text-[13px] font-semibold text-foreground transition-all active:scale-[0.98] disabled:opacity-60"
          >
            {t("ipmAssistant.cuadernoChange.discard", "Descartar")}
          </button>
        </div>
      )}
    </div>
  );
}
